import { Button } from "@tql/ui-components-react/tql/buttons";
import { useState } from "react";

import styles from "./week-selector.module.css";

export type DateRange = {
	start: Date;
	end: Date;
};

export type RangeOption = "thisWeek" | "nextWeek" | "lastWeek";

type WeekSelectorProps = {
	readonly onRangeChange?: (range: DateRange, option: RangeOption) => void;
	readonly defaultOption?: RangeOption;
};

export function getDateRange(option: RangeOption): DateRange {
	const today = new Date();
	const dayOfWeek = today.getDay();

	let offsetDays: number;
	switch (option) {
		case "lastWeek": {
			offsetDays = -7;
			break;
		}
		case "nextWeek": {
			offsetDays = 7;
			break;
		}
		default: {
			offsetDays = 0;
			break;
		}
	}

	const start = new Date(today);
	start.setDate(today.getDate() - dayOfWeek + offsetDays);
	start.setHours(0, 0, 0, 0);
	const end = new Date(start);
	end.setDate(start.getDate() + 6);
	end.setHours(23, 59, 59, 999);
	return { start, end };
}

const rangeOptions: { key: RangeOption; label: string }[] = [
	{ key: "thisWeek", label: "This Week" },
	{ key: "nextWeek", label: "Next Week" },
	{ key: "lastWeek", label: "Last Week" },
];

export function WeekSelector({
	onRangeChange,
	defaultOption = "thisWeek",
}: WeekSelectorProps) {
	const [activeOption, setActiveOption] = useState<RangeOption>(defaultOption);

	const handleSelect = (option: RangeOption) => {
		setActiveOption(option);
		onRangeChange?.(getDateRange(option), option);
	};

	return (
		<div className={styles.weekSelector}>
			{rangeOptions.map(({ key, label }) => (
				<Button
					key={key}
					onClick={() => handleSelect(key)}
					variant={activeOption === key ? "primary" : "secondary"}
				>
					{label}
				</Button>
			))}
		</div>
	);
}
